import { useRef } from 'react'

import { Toolbar } from '@/components/ui/Toolbar'

import { useHotkeys } from '@tanstack/react-hotkeys'

import { Command } from '@/shared/commands/command.types'

import { gridSelectionActions, gridStateActions } from '../../grid/grid.store'
import { getCellActions } from '../cell.actions'
import { CellCommandContext } from '../cell.commands'
import { useCellKey } from '../cell.context'
import { useCellIsEditing, useCellIsSelected } from '../cell.hooks'
import { CellDomUtils } from '../cell.schema'

import { CellToolbarItems } from './CellToolbar.items'
import { CellToolbarTooltipRoot } from './CellToolbar.tooltip'

/**
 * Toolbar that opens above Cells, it is sticky and accessible.
 */
export function CellToolbar({
  children,
  onKeyTab,
  localCommands,
}: React.ComponentProps<'div'> & {
  onKeyTab?: () => void
  localCommands?: Command<CellCommandContext>[]
}) {
  const cellKey = useCellKey()

  const isSelected = useCellIsSelected()
  const isEditing = useCellIsEditing()

  const { deselect } = getCellActions(cellKey)

  const toolbarRef = useRef<HTMLDivElement | null>(null)

  useHotkeys(
    [
      {
        hotkey: 'Escape',
        callback: isEditing
          ? () => {
              gridStateActions.editor.discard()
            }
          : deselect,
      },
      {
        hotkey: 'Tab',
        callback: onKeyTab
          ? onKeyTab
          : () => {
              const cellElm = CellDomUtils.queryValue('data-cell-key', cellKey)

              gridSelectionActions.focus(cellElm)
            },
      },
    ],
    {
      target: toolbarRef,
    },
  )

  if (!isSelected && !isEditing) return null

  return (
    // {/* Virtual track for toolbar */}
    <div
      role="none"
      className="absolute inset-x-0 top-0 bottom-6 select-none pointer-events-none"
    >
      {/* Sticky toolbar anchor */}
      <div
        role="none"
        className="sticky z-20 top-(--headers-size) pointer-coarse:top-(--headers-size) left-0 right-0 h-0 flex justify-center"
      >
        <Toolbar
          ref={toolbarRef}
          aria-label="Cell toolbar"
          className="absolute top-0 -translate-y-full -mt-2 pointer-events-auto"
        >
          {children}

          {localCommands && <CellToolbarItems items={localCommands} />}
        </Toolbar>

        <CellToolbarTooltipRoot />
      </div>
    </div>
  )
}
